import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchAuthSession, signOut as amplifySignOut } from 'aws-amplify/auth';
import { ReactComponent as BrandIcon } from '../assets/BrandIcon.svg';
import logo4T from '../assets/4Thought-Technologies.png';

const LAST_UPDATED = 'April 6, 2026';

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl font-semibold text-gray-900 mb-4">{title}</h2>
    <div className="space-y-3 text-gray-600 leading-relaxed">{children}</div>
  </section>
);

const TermsOfService = () => {
  const [isSignedIn, setIsSignedIn] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAuthSession().then(session => {
      setIsSignedIn(!!session.tokens);
    }).catch(() => {});
  }, []);

  const handleSignOut = async () => {
    await amplifySignOut();
    navigate('/login');
  };

  return (
  <div className="min-h-screen bg-white">

    {/* Nav */}
    <header className="border-b border-gray-100">
      <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center space-x-2">
          <BrandIcon className="h-7 w-7" />
          <span className="text-xl font-bold text-gray-900 brand-wordmark">BookMarker</span>
        </Link>
        {isSignedIn
          ? <button onClick={handleSignOut} className="text-sm font-medium text-gray-600 hover:text-primary-600 transition-colors">Sign Out</button>
          : <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-primary-600 transition-colors">Sign In</Link>
        }
      </div>
    </header>

    {/* Content */}
    <main className="max-w-3xl mx-auto px-6 py-16">

      <div className="mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-3">Terms of Service</h1>
        <p className="text-sm text-gray-400">Last updated: {LAST_UPDATED}</p>
      </div>

      <Section title="Acceptance of Terms">
        <p>
          These Terms of Service ("Terms") govern your use of BookMarker, a service operated
          by 4Thought Technologies, LLC ("we", "us", or "our"). By creating an account or
          using BookMarker in any way, you agree to be bound by these Terms. If you do not
          agree, please do not use the service.
        </p>
        <p>
          We may update these Terms from time to time. We will notify you of material changes
          by email and by updating the "Last updated" date above. Your continued use of
          BookMarker after any changes constitutes acceptance of the revised Terms.
        </p>
      </Section>

      <Section title="Description of Service">
        <p>
          BookMarker is a web-based service that allows you to save, organise, and manage
          URLs and web content ("bookmarks"). The service includes features such as automatic
          content summarisation, tag suggestions, collections, and optional sharing
          capabilities. We reserve the right to modify, suspend, or discontinue any feature
          of the service at any time with reasonable notice where practicable.
        </p>
      </Section>

      <Section title="Eligibility and Account Registration">
        <p>
          You must be at least 13 years of age to use BookMarker. By creating an account you
          represent that you meet this requirement.
        </p>
        <p>
          You are responsible for maintaining the confidentiality of your account credentials
          and for all activity that occurs under your account. You agree to notify us
          immediately at <a href="mailto:support@bookmarker.cloud" className="text-primary-600 hover:underline">support@bookmarker.cloud</a> if
          you believe your account has been compromised.
        </p>
        <p>
          You may not create more than one account per person or create an account on behalf
          of someone else without their consent. We reserve the right to refuse service,
          terminate accounts, or remove content at our discretion.
        </p>
      </Section>

      <Section title="Your Content">
        <p>
          BookMarker allows you to save URLs and associated content ("Your Content"). You
          retain all ownership rights to Your Content. By using the service, you grant us a
          limited, non-exclusive licence to store, process, and display Your Content solely
          to provide the service to you.
        </p>
        <p>
          You are solely responsible for Your Content and represent that you have the right
          to save and store any content you submit to BookMarker. You agree not to use
          BookMarker to save or share content that:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-2">
          <li>Infringes any copyright, trademark, or other intellectual property right</li>
          <li>Is unlawful, abusive, harassing, threatening, or fraudulent</li>
          <li>Contains malware, viruses, or other harmful code</li>
          <li>Violates the privacy of others</li>
        </ul>
      </Section>

      <Section title="Acceptable Use">
        <p>You agree to use BookMarker only for lawful purposes and in accordance with these Terms. You agree not to:</p>
        <ul className="list-disc list-inside space-y-2 ml-2">
          <li>Use the service in any way that could disable, overburden, or impair it</li>
          <li>Attempt to gain unauthorised access to any part of the service or its infrastructure</li>
          <li>Use automated scripts, bots, or scraping tools against the service without our written permission</li>
          <li>Circumvent any security or access-control features of the service</li>
          <li>Impersonate any person or entity or misrepresent your affiliation with any person or entity</li>
          <li>Use the service to transmit unsolicited communications (spam)</li>
        </ul>
      </Section>

      <Section title="Shared and Public Content">
        <p>
          BookMarker allows you to share collections via a public link and to create a public
          profile. When you share content publicly, you understand and agree that:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-2">
          <li>The content becomes accessible to anyone with the link, including people who are not registered users of BookMarker</li>
          <li>We are not responsible for how third parties access or use publicly shared content</li>
          <li>You can revoke sharing at any time, after which the content will no longer be publicly accessible</li>
        </ul>
      </Section>

      <Section title="Our Intellectual Property">
        <p>
          The BookMarker service, including its design, software, branding, and all content
          we create, is owned by 4Thought Technologies, LLC and is protected by intellectual
          property laws. These Terms do not grant you any right to use our trademarks, logos,
          or other proprietary materials without our prior written consent.
        </p>
      </Section>

      <Section title="Privacy">
        <p>
          Your use of BookMarker is also governed by our{' '}
          <Link to="/privacy" className="text-primary-600 hover:underline">Privacy Policy</Link>,
          which is incorporated into these Terms by reference. By using BookMarker you agree
          to the collection and use of your information as described in our Privacy Policy.
        </p>
      </Section>

      <Section title="Service Availability">
        <p>
          We strive to provide a reliable service but do not guarantee that BookMarker will
          be available at all times or free from errors. We may perform maintenance, upgrades,
          or experience outages. We will make reasonable efforts to provide advance notice of
          planned downtime where possible.
        </p>
        <p>
          We reserve the right to suspend or terminate the service, or your access to it,
          at any time for any reason, including violation of these Terms. We will make
          reasonable efforts to give you notice and an opportunity to export your data before
          doing so, except where prohibited by law or where immediate action is required to
          protect the security of the service or other users.
        </p>
      </Section>

      <Section title="Disclaimer of Warranties">
        <p>
          BookMarker is provided "as is" and "as available" without warranties of any kind,
          either express or implied, including but not limited to implied warranties of
          merchantability, fitness for a particular purpose, or non-infringement. We do not
          warrant that the service will meet your requirements, operate without interruption,
          or be error-free.
        </p>
        <p>
          AI-generated content (summaries, tags, and categories) is produced automatically
          and may contain inaccuracies. You should not rely on AI-generated content for
          decisions requiring precision or professional judgement.
        </p>
      </Section>

      <Section title="Limitation of Liability">
        <p>
          To the fullest extent permitted by applicable law, 4Thought Technologies, LLC and
          its affiliates, officers, employees, and agents shall not be liable for any
          indirect, incidental, special, consequential, or punitive damages arising out of
          or related to your use of BookMarker, even if we have been advised of the
          possibility of such damages.
        </p>
        <p>
          Our total liability to you for any claim arising from or related to these Terms or
          the service shall not exceed the amount you paid us in the twelve months preceding
          the claim, or USD $10 if you have not made any payments.
        </p>
      </Section>

      <Section title="Indemnification">
        <p>
          You agree to indemnify, defend, and hold harmless 4Thought Technologies, LLC and
          its affiliates from and against any claims, damages, losses, and expenses (including
          reasonable legal fees) arising from your use of BookMarker, Your Content, or your
          violation of these Terms.
        </p>
      </Section>

      <Section title="Governing Law">
        <p>
          These Terms are governed by and construed in accordance with the laws of the State
          of Oregon, United States, without regard to its conflict of law provisions. Any
          dispute arising from these Terms shall be subject to the exclusive jurisdiction of
          the courts located in Oregon.
        </p>
      </Section>

      <Section title="Contact Us">
        <p>
          If you have questions about these Terms, please contact us at:
        </p>
        <address className="not-italic mt-3 p-4 bg-gray-50 rounded-xl border border-gray-200 text-sm">
          <strong className="text-gray-800">4Thought Technologies, LLC</strong><br />
          Email: <a href="mailto:legal@bookmarker.cloud" className="text-primary-600 hover:underline">legal@bookmarker.cloud</a>
        </address>
      </Section>

    </main>

    {/* Footer */}
    <footer className="border-t border-gray-100 py-8">
      <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-400">
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <img src={logo4T} alt="4Thought Technologies" className="h-6 opacity-70" />
          <span>© {new Date().getFullYear()} 4Thought Technologies, LLC</span>
        </div>
        <div className="flex items-center gap-6">
          <Link to="/privacy" className="hover:text-gray-600 transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-gray-600 transition-colors">Terms of Service</Link>
          {isSignedIn
            ? <button onClick={handleSignOut} className="hover:text-gray-600 transition-colors">Sign Out</button>
            : <Link to="/login" className="hover:text-gray-600 transition-colors">Sign In</Link>
          }
        </div>
      </div>
    </footer>

  </div>
  );
};

export default TermsOfService;
